import { buildTaxSummaryDisplay, resolveTaxSummaryBlock } from '../../../new-elite-landing/taxSummaryDisplay'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import type { EliteInsights, TaxSummaryBlock } from '../../../../types/misc.types'
import type { AdviceCardStat, TaxSummaryAdviceCardDisplay } from '../../types/adviceCards.types'
import {
  TAX_SUMMARY_ADVANCE_TAX_LIABILITY_THRESHOLD,
  TAX_SUMMARY_ADVANCE_TAX_RATE,
  TAX_SUMMARY_MASKED_AMOUNT,
  TAX_SUMMARY_MONTH_SHORT_NAMES
} from '../../constants/taxSummary'

export type { TaxSummaryAdviceCardDisplay } from '../../types/adviceCards.types'


function shouldShowAdvanceTax(taxLiability: number): boolean {
  return taxLiability >= TAX_SUMMARY_ADVANCE_TAX_LIABILITY_THRESHOLD
}

function formatTaxPayableValue(taxLiability: number, masked: boolean): string {
  if (masked) {
    return TAX_SUMMARY_MASKED_AMOUNT
  }
  if (taxLiability === 0) {
    return 'No'
  }
  return roundOffAndFormatNumberWithCurrency(taxLiability) as string
}

function formatAdvanceTaxValue(taxLiability: number, masked: boolean): string {
  if (masked) {
    return TAX_SUMMARY_MASKED_AMOUNT
  }
  return roundOffAndFormatNumberWithCurrency(taxLiability * TAX_SUMMARY_ADVANCE_TAX_RATE) as string
}

function formatTaxPayableLabel(financialYear: string): string {
  const normalizedYear = financialYear.startsWith('FY ') ? financialYear.slice(3) : financialYear
  if (!normalizedYear) {
    return 'tax payable'
  }
  return `tax payable (FY ${normalizedYear})`
}

function inferPlanYearFromFinancialYear(financialYear: string): number | undefined {
  const normalizedYear = financialYear.startsWith('FY ') ? financialYear.slice(3) : financialYear
  const parts = normalizedYear.split('-')
  if (parts.length !== 2) {
    return undefined
  }

  const endYear = parts[1]
  if (endYear.length === 2) {
    return 2000 + Number(endYear)
  }
  return Number(endYear)
}

function getMonthShortName(monthNum: number): string {
  if (monthNum < 1 || monthNum > 12) {
    return 'Feb'
  }
  return TAX_SUMMARY_MONTH_SHORT_NAMES[monthNum - 1]
}

function buildTaxHarvestingPlanStat(firstPlanMonth: number | undefined, financialYear: string): AdviceCardStat {
  const monthName = getMonthShortName(firstPlanMonth ?? 2)
  const planYear = inferPlanYearFromFinancialYear(financialYear) ?? 2027
  return {
    value: 'Tax harvesting plan',
    label: `in ${monthName} ${planYear}`
  }
}

function buildHighlightStat(block: TaxSummaryBlock): AdviceCardStat | undefined {
  const highlight = buildTaxSummaryDisplay(block).summaryHighlight
  if (!highlight) {
    return undefined
  }
  return { label: highlight, emphasize: true }
}

function buildLockedTaxSummaryStats(block: TaxSummaryBlock, firstPlanMonth: number | undefined): AdviceCardStat[] {
  const stats: AdviceCardStat[] = []
  const taxLiability = block.taxLiability ?? 0
  const financialYear = block.financialYear ?? ''

  if (shouldShowAdvanceTax(taxLiability)) {
    stats.push({
      value: formatAdvanceTaxValue(taxLiability, true),
      label: 'advance tax due'
    })
  }

  stats.push({
    value: formatTaxPayableValue(taxLiability, true),
    label: formatTaxPayableLabel(financialYear)
  })
  stats.push(buildTaxHarvestingPlanStat(firstPlanMonth, financialYear))

  return stats
}

function buildEliteTaxSummaryStats(block: TaxSummaryBlock, firstPlanMonth: number | undefined): AdviceCardStat[] {
  const stats: AdviceCardStat[] = []
  const taxLiability = block.taxLiability ?? 0
  const financialYear = block.financialYear ?? ''

  if (shouldShowAdvanceTax(taxLiability)) {
    stats.push({
      value: formatAdvanceTaxValue(taxLiability, false),
      label: 'advance tax due'
    })
  }

  stats.push({
    value: formatTaxPayableValue(taxLiability, false),
    label: formatTaxPayableLabel(financialYear)
  })

  const highlightStat = buildHighlightStat(block)
  if (highlightStat) {
    stats.push(highlightStat)
    return stats
  }

  stats.push(buildTaxHarvestingPlanStat(firstPlanMonth, financialYear))
  return stats
}

function isLockedTaxSummaryPreview(
  block: TaxSummaryBlock,
  userElite: boolean,
  isUserPrime: boolean
): boolean {
  if (isUserPrime) {
    return false
  }
  if (!userElite) {
    return true
  }
  return `${block.summaryType ?? ''}` === 'NON_ELITE'
}

export function buildTaxSummaryAdviceCardDisplay(
  eliteInsights: EliteInsights | null | undefined,
  isUserPrime: boolean
): TaxSummaryAdviceCardDisplay {
  if (!eliteInsights) {
    return { stats: [], insightTone: 'orange' }
  }

  const taxSummary = resolveTaxSummaryBlock(eliteInsights.taxSummary, eliteInsights.taxSummaryCta)
  if (!taxSummary) {
    return { stats: [], insightTone: 'orange' }
  }

  const userElite = eliteInsights.userElite ?? false
  const firstPlanMonth = eliteInsights.firstPlanMonth

  if (isLockedTaxSummaryPreview(taxSummary, userElite, isUserPrime)) {
    return {
      stats: buildLockedTaxSummaryStats(taxSummary, firstPlanMonth),
      insightTone: 'orange'
    }
  }

  return {
    stats: buildEliteTaxSummaryStats(taxSummary, firstPlanMonth),
    insightTone: 'orange'
  }
}
